import { cn } from "@/lib/utils";

/**
 * A state, worn as a chip.
 *
 * The stage chip's sibling. Green for on, grey for off, and **the text carries the state**:
 * colour is decoration here exactly as it is on `StageChip`, because a reader who cannot tell
 * the two hues apart must still be able to read the column.
 *
 * `tone` exists for the one column whose states are not a pair. A client is active or it is
 * not; a *user* is invited, active or deactivated, and an invitation nobody has accepted is
 * neither of the other two — it wants attention rather than approval or silence. Passing a
 * tone names that third reading explicitly instead of forcing a boolean to carry it.
 */
export type ChipTone = "positive" | "neutral" | "warning";

const TONE_CLASS: Record<ChipTone, string> = {
  positive: "bg-success/15 text-success",
  neutral: "bg-muted text-muted-foreground",
  warning: "bg-warning/15 text-warning",
};

export function StatusChip({
  label,
  on,
  tone,
  className,
}: {
  label: string;
  /** The two-state reading, and the default source of the tone. */
  on: boolean;
  /** Overrides what `on` would imply. For a third state that is neither. */
  tone?: ChipTone;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium",
        TONE_CLASS[tone ?? (on ? "positive" : "neutral")],
        className,
      )}
    >
      {label}
    </span>
  );
}
